// ═══════════════════════════════════════════════════════════════
// PlanSelection.jsx — Plan selection UI
// Shows Personal + Personal+ cards AND Professional + Enterprise
// org plan cards with org name + seat count inputs.
// Redirects to Stripe Checkout via stripeAdapter.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useCallback } from 'react';
import { Check, X, Crown, Zap, Building2, Shield, ArrowRight, Loader2, Users, Minus, Plus, MessageSquare, Heart, Vote, LogOut } from 'lucide-react';
import { PERSONAL_PLANS, ORG_PLANS, DEFAULT_CONFIG } from './BillingConfig.js';

/**
 * <PlanSelection
 *   user={{ id, name, email }}
 *   stripeAdapter={adapter}
 *   onPlanSelected={() => {}}
 *   onCreateTenant={async (overrides) => tenantId}  // required for org plans
 *   logoUrl={OWL_LOGO}            // optional — displayed in header
 * />
 */
function PlanSelection({ user, stripeAdapter, onPlanSelected, onCreateTenant, onSelectFree, onLogout, logoUrl, authUid }) {
  const [billingCycle, setBillingCycle] = useState('annual');
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [planCategory, setPlanCategory] = useState('personal'); // 'free' | 'personal' | 'org'

  // Org plan form state
  const [orgName, setOrgName] = useState('');
  const [seatCount, setSeatCount] = useState(5);
  const [orgError, setOrgError] = useState('');

  const handleSelectPersonalPlan = useCallback(async (plan) => {
    if (loadingPlan) return;
    setLoadingPlan(plan.key);

    const priceKey = billingCycle === 'annual' ? plan.priceKeyAnnual : plan.priceKeyMonthly;

    try {
      const data = await stripeAdapter.createCheckoutSession({
        priceKey,
        userId: user.id,
        successUrl: `${window.location.origin}?billing=success`,
        cancelUrl: `${window.location.origin}?billing=cancelled`,
      });

      if (data.url) {
        window.location.href = data.url;
      } else {
        console.error('No checkout URL returned:', data);
        setLoadingPlan(null);
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      setLoadingPlan(null);
    }
  }, [billingCycle, user.id, stripeAdapter, loadingPlan]);

  const handleSelectOrgPlan = useCallback(async (plan) => {
    if (loadingPlan) return;

    // Validate org name
    const trimmedName = orgName.trim();
    if (!trimmedName) {
      setOrgError('Please enter your organization name.');
      return;
    }
    if (trimmedName.length < 2) {
      setOrgError('Organization name must be at least 2 characters.');
      return;
    }

    // Validate seat count
    if (seatCount < plan.minSeats) {
      setOrgError(`${plan.name} requires a minimum of ${plan.minSeats} seats.`);
      return;
    }

    setOrgError('');
    setLoadingPlan(plan.key);

    const priceKey = billingCycle === 'annual' ? plan.priceKeyAnnual : plan.priceKeyMonthly;

    try {
      // Step 1: Create tenant (pending status)
      if (!onCreateTenant) {
        console.error('onCreateTenant callback is required for org plans');
        setLoadingPlan(null);
        return;
      }

      const tenantId = await onCreateTenant({
        name: trimmedName,
        plan: plan.key,
        maxSeats: seatCount,
        status: 'pending',
        createdBy: user.id,
        billing: {
          contactEmail: user.email || '',
          contactName: user.name || '',
        },
      });

      if (!tenantId) {
        setOrgError('Failed to create organization. Please try again.');
        setLoadingPlan(null);
        return;
      }

      // Step 2: Create checkout session with tenantId + authUid for claims
      const data = await stripeAdapter.createCheckoutSession({
        priceKey,
        userId: user.id,
        authUid,
        tenantId,
        seats: seatCount,
        successUrl: `${window.location.origin}?billing=org-success&tenantId=${tenantId}`,
        cancelUrl: `${window.location.origin}?billing=org-cancelled&tenantId=${tenantId}`,
      });

      if (data.url) {
        window.location.href = data.url;
      } else {
        console.error('No checkout URL returned:', data);
        setOrgError(data.error || 'Failed to create checkout. Please try again.');
        setLoadingPlan(null);
      }
    } catch (error) {
      console.error('Failed to create org checkout session:', error);
      setOrgError('Something went wrong. Please try again.');
      setLoadingPlan(null);
    }
  }, [billingCycle, user, stripeAdapter, loadingPlan, orgName, seatCount, onCreateTenant]);

  const annualSavings = DEFAULT_CONFIG.annualSavingsPercent;

  const ICON_MAP = {
    'personal': Zap,
    'personal-plus': Crown,
    'professional': Building2,
    'enterprise': Shield,
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-stone-100 flex flex-col items-center px-4 py-8">
      {/* Header */}
      <div className="text-center mb-6">
        {logoUrl && (
          <img src={logoUrl} alt="" className="w-16 h-16 mx-auto mb-4 rounded-2xl" />
        )}
        <h1 className="text-3xl font-bold text-stone-800 mb-2">Choose Your Plan</h1>
        <p className="text-stone-500 max-w-md mx-auto">
          Get started with free tools, or unlock the full conflict resolution platform.
        </p>
      </div>

      {/* Plan Category Toggle */}
      <div className="flex items-center gap-3 mb-4 bg-white rounded-full p-1.5 shadow-sm border border-stone-200">
        <button
          onClick={() => setPlanCategory('free')}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
            planCategory === 'free'
              ? 'bg-stone-800 text-white shadow-sm'
              : 'text-stone-500 hover:text-stone-700'
          }`}
        >
          Free Tools
        </button>
        <button
          onClick={() => setPlanCategory('personal')}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
            planCategory === 'personal'
              ? 'bg-stone-800 text-white shadow-sm'
              : 'text-stone-500 hover:text-stone-700'
          }`}
        >
          Personal
        </button>
        <button
          onClick={() => setPlanCategory('org')}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
            planCategory === 'org'
              ? 'bg-stone-800 text-white shadow-sm'
              : 'text-stone-500 hover:text-stone-700'
          }`}
        >
          Organizations
        </button>
      </div>

      {/* Billing Cycle Toggle — hidden for free tools */}
      <div className={`flex items-center gap-3 mb-8 bg-white rounded-full p-1.5 shadow-sm border border-stone-200 ${planCategory === 'free' ? 'hidden' : ''}`}>
        <button
          onClick={() => setBillingCycle('monthly')}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
            billingCycle === 'monthly'
              ? 'bg-amber-700 text-white shadow-sm'
              : 'text-stone-500 hover:text-stone-700'
          }`}
        >
          Monthly
        </button>
        <button
          onClick={() => setBillingCycle('annual')}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
            billingCycle === 'annual'
              ? 'bg-amber-700 text-white shadow-sm'
              : 'text-stone-500 hover:text-stone-700'
          }`}
        >
          Annual
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            billingCycle === 'annual'
              ? 'bg-amber-500 text-stone-900'
              : 'bg-green-100 text-green-700'
          }`}>
            Save {annualSavings}%
          </span>
        </button>
      </div>

      {/* Free Tools */}
      {planCategory === 'free' && (
        <div className="max-w-lg w-full">
          <div className="bg-white rounded-2xl shadow-md border-2 border-emerald-300 p-6 flex flex-col">
            <div className="text-center mb-6">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center bg-emerald-100 text-emerald-600">
                <MessageSquare className="w-6 h-6" />
              </div>
              <h2 className="text-xl font-bold text-stone-800">Free Communication Tools</h2>
              <p className="text-sm text-stone-500 mt-1">No credit card required</p>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3">
                <MessageSquare className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <span className="text-sm text-stone-700">Say It Your Way: 3 sessions per week</span>
              </div>
              <div className="flex items-center gap-3">
                <Heart className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <span className="text-sm text-stone-700">Mood Journal: daily check-ins</span>
              </div>
              <div className="flex items-center gap-3">
                <Vote className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <span className="text-sm text-stone-700">Vote Mode: 2 active votes</span>
              </div>
              <div className="flex items-center gap-3">
                <X className="w-4 h-4 text-stone-300 flex-shrink-0" />
                <span className="text-sm text-stone-500">No conflict resolution process</span>
              </div>
              <div className="flex items-center gap-3">
                <X className="w-4 h-4 text-stone-300 flex-shrink-0" />
                <span className="text-sm text-stone-500">No mentor access</span>
              </div>
            </div>

            <div className="text-center mb-4">
              <span className="text-4xl font-bold text-stone-800">$0</span>
              <span className="text-stone-500 ml-1">forever</span>
            </div>

            <button
              onClick={onSelectFree}
              disabled={loadingPlan !== null}
              className="w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 bg-emerald-700 text-white hover:bg-emerald-800 shadow-md hover:shadow-lg"
            >
              Continue Free
              <ArrowRight className="w-4 h-4" />
            </button>

            <p className="text-xs text-stone-500 text-center mt-4">
              You can upgrade to a paid plan anytime from your dashboard.
            </p>
          </div>
        </div>
      )}

      {/* Personal Plan Cards */}
      {planCategory === 'personal' && (
        <div className="flex flex-col sm:flex-row gap-6 max-w-3xl w-full">
          {PERSONAL_PLANS.map((plan) => {
            const Icon = ICON_MAP[plan.key] || Zap;
            const price = billingCycle === 'annual' ? plan.annualMonthlyPrice : plan.monthlyPrice;
            const isLoading = loadingPlan === plan.key;
            const isDisabled = loadingPlan !== null;

            return (
              <div
                key={plan.key}
                className={`flex-1 bg-white rounded-2xl shadow-md border-2 p-6 flex flex-col relative transition-all ${
                  plan.popular
                    ? 'border-amber-500 ring-2 ring-amber-200'
                    : 'border-stone-200 hover:border-amber-300'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-stone-900 text-xs font-bold px-4 py-1 rounded-full shadow-sm">
                    MOST POPULAR
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center ${
                    plan.popular ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-600'
                  }`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h2 className="text-xl font-bold text-stone-800">{plan.name}</h2>
                  <p className="text-sm text-stone-500 mt-1">{plan.tagline}</p>
                </div>

                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-stone-800">${price.toFixed(2)}</span>
                    <span className="text-stone-500">/mo</span>
                  </div>
                  {billingCycle === 'annual' && (
                    <p className="text-sm text-stone-500 mt-1">
                      ${plan.annualTotalPrice.toFixed(2)} billed annually
                    </p>
                  )}
                </div>

                <div className="flex-1 space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      {feature.included ? (
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <X className="w-4 h-4 text-stone-300 flex-shrink-0" />
                      )}
                      <span className={`text-sm ${feature.included ? 'text-stone-700' : 'text-stone-500'}`}>
                        {feature.label}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => handleSelectPersonalPlan(plan)}
                  disabled={isDisabled}
                  className={`w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                    plan.popular
                      ? 'bg-amber-700 text-white hover:bg-amber-800 shadow-md hover:shadow-lg'
                      : 'bg-stone-800 text-white hover:bg-stone-900'
                  } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Redirecting to checkout...
                    </>
                  ) : (
                    <>
                      Start Free Trial
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Org Plan Cards */}
      {planCategory === 'org' && (
        <>
          {/* Org Name + Seat Count */}
          <div className="w-full max-w-3xl mb-6 bg-white rounded-2xl shadow-sm border border-stone-200 p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Organization Name</label>
                <input
                  type="text"
                  value={orgName}
                  onChange={(e) => { setOrgName(e.target.value); setOrgError(''); }}
                  placeholder="Acme Corp"
                  className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">
                  Number of Seats
                </label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSeatCount(s => Math.max(5, s - 1))}
                    className="p-2 border border-stone-300 rounded-lg hover:bg-stone-50 transition-colors"
                  >
                    <Minus className="w-4 h-4 text-stone-600" />
                  </button>
                  <input
                    type="number"
                    value={seatCount}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val) && val >= 1) setSeatCount(val);
                    }}
                    className="w-20 text-center px-3 py-2.5 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                    min="5"
                  />
                  <button
                    onClick={() => setSeatCount(s => s + 1)}
                    className="p-2 border border-stone-300 rounded-lg hover:bg-stone-50 transition-colors"
                  >
                    <Plus className="w-4 h-4 text-stone-600" />
                  </button>
                  <span className="text-xs text-stone-500 ml-1">
                    <Users className="w-3.5 h-3.5 inline mr-1" />
                    seats
                  </span>
                </div>
              </div>
            </div>
            {orgError && (
              <p className="mt-3 text-sm text-red-700 bg-red-50 px-3 py-2 rounded-lg">{orgError}</p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-6 max-w-3xl w-full">
            {ORG_PLANS.map((plan) => {
              const Icon = ICON_MAP[plan.key] || Building2;
              const price = billingCycle === 'annual' ? plan.annualMonthlyPrice : plan.monthlyPrice;
              const isLoading = loadingPlan === plan.key;
              const isDisabled = loadingPlan !== null;
              const meetsMinSeats = seatCount >= plan.minSeats;
              const isEnterprise = plan.key === 'enterprise';
              const totalMonthly = price * seatCount;

              return (
                <div
                  key={plan.key}
                  className={`flex-1 bg-white rounded-2xl shadow-md border-2 p-6 flex flex-col relative transition-all ${
                    isEnterprise
                      ? 'border-stone-800 ring-2 ring-stone-300'
                      : 'border-stone-200 hover:border-amber-300'
                  }`}
                >
                  {isEnterprise && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-stone-800 text-white text-xs font-bold px-4 py-1 rounded-full shadow-sm">
                      FULL FEATURED
                    </div>
                  )}

                  <div className="text-center mb-6">
                    <div className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center ${
                      isEnterprise ? 'bg-stone-100 text-stone-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <h2 className="text-xl font-bold text-stone-800">{plan.name}</h2>
                    <p className="text-sm text-stone-500 mt-1">{plan.tagline}</p>
                  </div>

                  <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-bold text-stone-800">${price.toFixed(2)}</span>
                      <span className="text-stone-500">/seat/mo</span>
                    </div>
                    <p className="text-sm text-stone-500 mt-1">
                      {seatCount} seat{seatCount !== 1 ? 's' : ''} = <span className="font-medium text-stone-700">${totalMonthly.toFixed(2)}/mo</span>
                    </p>
                    {billingCycle === 'annual' && (
                      <p className="text-xs text-stone-500 mt-0.5">
                        ${(plan.annualTotalPrice * seatCount).toFixed(2)} billed annually
                      </p>
                    )}
                    <p className="text-xs text-stone-500 mt-1">
                      Minimum {plan.minSeats} seats
                    </p>
                  </div>

                  <div className="flex-1 space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-3">
                        {feature.included ? (
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        ) : (
                          <X className="w-4 h-4 text-stone-300 flex-shrink-0" />
                        )}
                        <span className={`text-sm ${feature.included ? 'text-stone-700' : 'text-stone-500'}`}>
                          {feature.label}
                        </span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => handleSelectOrgPlan(plan)}
                    disabled={isDisabled || !meetsMinSeats}
                    className={`w-full py-3 px-4 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                      isEnterprise
                        ? 'bg-stone-800 text-white hover:bg-stone-900'
                        : 'bg-amber-700 text-white hover:bg-amber-800 shadow-md hover:shadow-lg'
                    } ${(isDisabled || !meetsMinSeats) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Setting up organization...
                      </>
                    ) : !meetsMinSeats ? (
                      `Requires ${plan.minSeats}+ seats`
                    ) : (
                      <>
                        Start Free Trial
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Footer Notes */}
      <div className="mt-8 text-center text-sm text-stone-500 max-w-lg">
        <p>{DEFAULT_CONFIG.trialDays}-day free trial on paid plans. Credit card required.</p>
        <p className="mt-1">{DEFAULT_CONFIG.refundPolicy}</p>
        {onLogout && (
          <button
            onClick={onLogout}
            className="mt-4 inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-600 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Log out
          </button>
        )}
        <p className="mt-4">
          <a
            href="https://theargumentor.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs sm:text-sm text-amber-700 hover:text-amber-800 hover:underline"
          >
            Curious? Read all about The ArguMentor &rarr;
          </a>
        </p>
      </div>
    </div>
  );
}

export default PlanSelection;
